import { Observable, from } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { IImageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { ProgressBarService } from 'src/app/features/shared/progress-bar/progress-bar.service';
import { TPhotoName } from 'src/app/infrastructure/models/image-type';
import { unwrapData } from 'src/app/infrastructure/functions/unwrap-data.operator';

// Upload een foto (zelfde procedure als de fotolijst: PATCH /api/Photo/image-save) en koppelt 'm
// meteen als hoofdfoto van de parent (PATCH /api/Photo/main) — gebruikt door de hoofdfoto-box op
// elke *DetailsComponent (Project/Notice/Domain/Contact). Gedeeld i.p.v. per component gedupliceerd:
// het is een stateful twee-staps workflow, geen paar losse regels.
//
// Voortgang: determinate 2-staps (call-count, geen bytes) op de footer-progressbar — 0% bij start,
// 50% ná image-save, 100% ná main. Alleen Mode/ProgressValue; Active schakelt via het screen-lock
// van de aanroeper mee (zie PageLockService).
export function photoMainUpload$(
    apiService: ApiService,
    progressBarService: ProgressBarService,
    file: File,
    imageType: TPhotoName,
    guid: string | undefined,
    routeKeys: { dauGuid?: string | null; pamGuid?: string | null } | null,
    // UserPhoto als domain-specifieke profielfoto (DomainUser.ProfileFileBoxId) i.p.v. de globale
    // Home-hoofdfoto — zie PhotoController.MainPhoto (case UserPhoto/AsProfilePhoto).
    asProfilePhoto: boolean = false,
): Observable<IImageBoxDto> {
    const __formData = new FormData();
    __formData.append('File', file, file.name);

    const __dauGuid = routeKeys?.dauGuid ?? undefined;
    const __pamGuid = routeKeys?.pamGuid ?? undefined;

    progressBarService.Mode = 'determinate';
    progressBarService.ProgressValue = 0;

    return from(apiService.PATCH('/api/Photo/image-save', {
        params: { query: { imageType, guid, domainAUserGuid: __dauGuid, projectAMemberGuid: __pamGuid } },
        body: __formData as any,
    })).pipe(
        unwrapData<IImageBoxDto>(),
        tap(() => progressBarService.ProgressValue = 50),
        switchMap(dto => from(apiService.PATCH('/api/Photo/main', {
            params: { query: { domainAUserGuid: __dauGuid, projectAMemberGuid: __pamGuid } },
            body: { imageType, guid: dto.guid, asProfilePhoto },
        })).pipe(map(() => dto))),
        tap(() => progressBarService.ProgressValue = 100),
        // Terug naar de rusttoestand (zelfde cleanup als ImageTablistComponent na een batch),
        // óók bij een fout of unsubscribe; de balk zelf verdwijnt zodra Active weer uit gaat.
        finalize(() => {
            progressBarService.Mode = 'indeterminate';
            progressBarService.ProgressValue = 0;
        }),
    );
}
